import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { trackPageView } from '../services/page-view-tracking.service';
import { getLogger } from '../utils/logger';

const logger = getLogger('usePageViewTracking');

// Map routes to friendly page titles
const PAGE_TITLES: Record<string, string> = {
  '/': 'Home',
  '/home': 'Home',
  '/about': 'About',
  '/pricing': 'Pricing',
  '/freebie-downloadables': 'Freebie Downloadables',
  '/profile': 'Profile',
  '/dashboard': 'Dashboard',
  '/callback': 'Callback',
};

/**
 * Hook to track page views automatically on route changes
 * Tracks both authenticated and unauthenticated page views
 */
export const usePageViewTracking = () => {
  const location = useLocation();
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();

  useEffect(() => {
    const handlePageView = async () => {
      try {
        const pagePath = location.pathname;
        const pageTitle = PAGE_TITLES[pagePath] || pagePath;

        // Get access token if user is authenticated
        let token: string | undefined;
        if (isAuthenticated) {
          try {
            token = await getAccessTokenSilently();
          } catch (error) {
            logger.warn('Failed to get access token for page view tracking', error);
            // Continue without token - track as anonymous user
          }
        }

        // Track the page view
        await trackPageView(pagePath, pageTitle, token);
        logger.debug('Page view tracked', { pagePath, pageTitle, isAuthenticated });

      } catch (error) {
        logger.error('Failed to track page view', error);
      }
    };

    handlePageView();
  }, [location.pathname, isAuthenticated, getAccessTokenSilently]);
};
